/**
 * 근무시간 계산. 서버와 화면이 같은 값을 내야 하므로 여기 한 곳에만 둔다.
 *
 * work_sessions에 남는 것은 재실 시간(출근~퇴근)이고 근무시간이 아니다.
 * 하루 9시간 자리를 지켰다면 그 안에 휴게 1시간이 들어 있다.
 */

export interface WorkSpan {
  startedAt: string;
  endedAt: string | null;
}

/** 근로기준법 제54조의 단계. 재실 시간을 기준으로 판정한다. */
export const HALF_BREAK_AFTER_MINUTES = 240;
export const FULL_BREAK_AFTER_MINUTES = 480;

function spanMinutes(from: string, to: string): number {
  return Math.max(0, Math.round((new Date(to).getTime() - new Date(from).getTime()) / 60_000));
}

/** 그날 재실 시간에 대해 규정상 주어지는 휴게. */
export function requiredBreakMinutes(presenceMinutes: number, breakMinutes: number): number {
  if (presenceMinutes >= FULL_BREAK_AFTER_MINUTES) return breakMinutes;
  if (presenceMinutes >= HALF_BREAK_AFTER_MINUTES) return Math.round(breakMinutes / 2);
  return 0;
}

/** 하루치 재실 시간. 진행 중인 세션은 지금까지로 친다. */
export function presenceMinutesForDay(spans: WorkSpan[], nowIso: string): number {
  return spans.reduce((total, span) => total + spanMinutes(span.startedAt, span.endedAt ?? nowIso), 0);
}

/**
 * 하루치 근무시간.
 *
 * 점심에 태그를 찍고 나갔다 온 사람은 세션이 둘로 나뉘고 그 사이 공백만큼
 * 휴게가 이미 재실에서 빠져 있다. 여기서 규정 휴게를 그대로 또 빼면 같은
 * 휴게를 두 번 차감하게 되므로, 공백으로 이미 빠진 만큼을 먼저 상계하고
 * 모자란 만큼만 뺀다. 하루 내내 한 번도 찍고 나가지 않았다면 공백이 0이라
 * 규정 휴게가 전액 차감된다.
 */
export function workedMinutesForDay(spans: WorkSpan[], breakMinutes: number, nowIso: string): number {
  if (spans.length === 0) return 0;
  const ranges = spans.map((span) => ({
    from: new Date(span.startedAt).getTime(),
    to: new Date(span.endedAt ?? nowIso).getTime(),
  }));
  const presence = presenceMinutesForDay(spans, nowIso);
  const dayStart = Math.min(...ranges.map((range) => range.from));
  const dayEnd = Math.max(...ranges.map((range) => range.to));
  const awayMinutes = Math.max(0, Math.round((dayEnd - dayStart) / 60_000) - presence);
  const outstanding = Math.max(0, requiredBreakMinutes(presence, breakMinutes) - awayMinutes);
  return Math.max(0, presence - outstanding);
}
